import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AreaChart, PieChart, BarChart, ChartContainer } from '@/components/charts';
import { useCommunicationsOverview, useCommunicationsTrends } from '../hooks';
import type { PeriodFilter } from '../api';
import {
  RefreshCw, Mail, MessageSquare, Phone, Bell, ChevronRight,
  TrendingUp, PieChartIcon, BarChart3
} from 'lucide-react';
import { cn } from '@/lib/utils';

type Period = PeriodFilter;

export default function CommunicationsOverviewPage() {
  const { t } = useTranslation('intelligence');
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('week');

  const { data: overview, isLoading: overviewLoading, refetch, isFetching } = useCommunicationsOverview(period);
  const { data: trends, isLoading: trendsLoading } = useCommunicationsTrends(period);

  const periods: { value: Period; label: string }[] = [
    { value: 'day', label: t('communications.periods.day', 'Today') },
    { value: 'week', label: t('communications.periods.week', 'This Week') },
    { value: 'month', label: t('communications.periods.month', 'This Month') },
    { value: 'quarter', label: t('communications.periods.quarter', 'This Quarter') },
  ];

  const formatNumber = (num: number | undefined) => {
    if (num === undefined || num === null) return '--';
    return num.toLocaleString();
  };

  const formatPercent = (value: number | undefined) => {
    if (value === undefined || value === null) return '--';
    return `${(Math.round(value * 10) / 10)}%`;
  };

  const goToChannel = (channel: string) => {
    navigate(`/intelligence/communications/${channel}`);
  };

  // Chart data
  const trendsChartData = trends?.map(item => ({
    name: item.date,
    email: item.email_total || 0,
    sms: item.sms_total || 0,
    calls: item.calls_total || 0,
    alerts: item.alerts_total || 0,
  })) || [];

  const distributionData = [
    { name: t('communications.channels.email', 'Email'), value: overview?.email?.total || 0, color: '#3498db' },
    { name: t('communications.channels.sms', 'SMS'), value: overview?.sms?.total || 0, color: '#9b59b6' },
    { name: t('communications.channels.calls', 'Calls'), value: overview?.calls?.total || 0, color: '#e67e22' },
    { name: t('communications.channels.alerts', 'Alert Sendouts'), value: overview?.alerts?.total || 0, color: '#27ae60' },
  ].filter(d => d.value > 0);

  const deliveryRatesData = [
    { name: t('communications.channels.email', 'Email'), value: overview?.email?.deliveryRate || 0, color: '#3498db' },
    { name: t('communications.channels.sms', 'SMS'), value: overview?.sms?.deliveryRate || 0, color: '#9b59b6' },
    { name: t('communications.channels.calls', 'Calls'), value: overview?.calls?.answerRate || 0, color: '#e67e22' },
    { name: t('communications.channels.alerts', 'Alerts'), value: overview?.alerts?.deliveryRate || 0, color: '#27ae60' },
  ];

  const channelCards = [
    {
      key: 'email',
      icon: Mail,
      color: 'blue',
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
      data: overview?.email,
      metrics: [
        { key: 'total', label: t('communications.metrics.total', 'Total'), format: formatNumber },
        { key: 'deliveryRate', label: t('communications.metrics.deliveryRate', 'Delivery Rate'), format: formatPercent },
        { key: 'openRate', label: t('communications.metrics.openRate', 'Open Rate'), format: formatPercent },
      ],
    },
    {
      key: 'sms',
      icon: MessageSquare,
      color: 'purple',
      bgColor: 'bg-purple-100',
      iconColor: 'text-purple-600',
      data: overview?.sms,
      metrics: [
        { key: 'total', label: t('communications.metrics.total', 'Total'), format: formatNumber },
        { key: 'deliveryRate', label: t('communications.metrics.deliveryRate', 'Delivery Rate'), format: formatPercent },
        { key: 'segments', label: t('communications.metrics.segments', 'Segments'), format: formatNumber },
      ],
    },
    {
      key: 'calls',
      icon: Phone,
      color: 'orange',
      bgColor: 'bg-orange-100',
      iconColor: 'text-orange-600',
      data: overview?.calls,
      metrics: [
        { key: 'total', label: t('communications.metrics.total', 'Total'), format: formatNumber },
        { key: 'answerRate', label: t('communications.metrics.answerRate', 'Answer Rate'), format: formatPercent },
        { key: 'completed', label: t('communications.metrics.completed', 'Completed'), format: formatNumber },
      ],
    },
    {
      key: 'alerts',
      icon: Bell,
      color: 'green',
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600',
      data: overview?.alerts,
      metrics: [
        { key: 'total', label: t('communications.metrics.total', 'Total'), format: formatNumber },
        { key: 'deliveryRate', label: t('communications.metrics.deliveryRate', 'Delivery Rate'), format: formatPercent },
        { key: 'ackRate', label: t('communications.metrics.ackRate', 'Ack Rate'), format: formatPercent },
      ],
    },
  ];

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('communications.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('communications.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border bg-white overflow-hidden">
            {periods.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  period === p.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            {t('common:refresh', 'Refresh')}
          </Button>
        </div>
      </div>

      {/* Channel Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {channelCards.map((channel) => {
          const Icon = channel.icon;
          return (
            <Card
              key={channel.key}
              className="bg-white cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => goToChannel(channel.key)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={cn('p-2 rounded-lg', channel.bgColor)}>
                      <Icon className={cn('h-5 w-5', channel.iconColor)} />
                    </div>
                    <span className="font-semibold text-gray-900">
                      {t(`communications.channels.${channel.key}`, channel.key)}
                    </span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {channel.metrics.map((metric) => (
                    <div key={metric.key} className="text-center">
                      {overviewLoading ? (
                        <Skeleton className="h-5 w-12 mx-auto mb-1" />
                      ) : (
                        <div className={cn(
                          'text-lg font-bold',
                          metric.key === 'deliveryRate' || metric.key === 'openRate' || metric.key === 'answerRate' || metric.key === 'ackRate'
                            ? 'text-green-600'
                            : 'text-gray-900'
                        )}>
                          {metric.format((channel.data as Record<string, number>)?.[metric.key])}
                        </div>
                      )}
                      <div className="text-xs text-gray-500">{metric.label}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row 1 - Volume Trends + Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center gap-2">
            <TrendingUp className="h-5 w-5 text-gray-500" />
            <CardTitle>{t('communications.charts.volumeTrends', 'Volume Trends')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={trendsLoading} height={300}>
              <AreaChart
                data={trendsChartData}
                series={[
                  { dataKey: 'email', name: t('communications.channels.email', 'Email'), color: '#3498db' },
                  { dataKey: 'sms', name: t('communications.channels.sms', 'SMS'), color: '#9b59b6' },
                  { dataKey: 'calls', name: t('communications.channels.calls', 'Calls'), color: '#e67e22' },
                  { dataKey: 'alerts', name: t('communications.channels.alerts', 'Alerts'), color: '#27ae60' },
                ]}
                height={270}
                stacked
              />
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-2">
            <PieChartIcon className="h-5 w-5 text-gray-500" />
            <CardTitle>{t('communications.charts.channelDistribution', 'Channel Distribution')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={overviewLoading} height={280}>
              {distributionData.length > 0 ? (
                <PieChart
                  data={distributionData}
                  height={250}
                  showLegend
                  innerRadius={50}
                />
              ) : (
                <div className="flex items-center justify-center h-[250px] text-gray-400">
                  <div className="text-center">
                    <PieChartIcon className="h-12 w-12 mx-auto mb-2" />
                    <p>{t('communications.noData', 'No data available')}</p>
                  </div>
                </div>
              )}
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 - Delivery Rates */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-2">
          <BarChart3 className="h-5 w-5 text-gray-500" />
          <CardTitle>{t('communications.charts.deliveryRates', 'Delivery Rates by Channel')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer loading={overviewLoading} height={280}>
            <BarChart
              data={deliveryRatesData}
              height={250}
              colors={['#3498db', '#9b59b6', '#e67e22', '#27ae60']}
              colorByValue
              horizontal
            />
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
}
